import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard as PaymentIcon, RefreshCw, DollarSign } from 'lucide-react';
import PaymentTable from '../../components/Payment/PaymentTable';
import PaymentFilters from '../../components/Payment/PaymentFilters';
import PaymentPagination from '../../components/Payment/PaymentPagination';
import PaymentDetails from '../../components/Payment/PaymentDetails';
import {
  getPayments,
  getPaymentFilters,
  getPaymentStats,
} from '../../services/paymentApi';

const Payment = () => {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState(null);
  const [availableFilters, setAvailableFilters] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    customer: '',
    status: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [paginationState, setPaginationState] = useState({
    page: 1,
    limit: 20,
  });

  const loadPayments = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getPayments({
        ...filters,
        page: paginationState.page,
        limit: paginationState.limit,
      });

      setPayments(result.payments);
      setPagination(result.pagination);
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, paginationState]);

  const loadStats = useCallback(async () => {
    try {
      const result = await getPaymentStats('7d');
      setStats(result);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  const loadFilters = useCallback(async () => {
    try {
      const result = await getPaymentFilters();
      setAvailableFilters(result);
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    loadStats();
    loadFilters();
  }, [loadStats, loadFilters]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPaginationState((prev) => ({ ...prev, page: 1 }));
  };

  const handleSort = (field) => {
    const newSortOrder =
      filters.sortBy === field && filters.sortOrder === 'desc' ? 'asc' : 'desc';
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder: newSortOrder,
    }));
  };

  const handlePageChange = (page) => {
    setPaginationState((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit) => {
    setPaginationState((prev) => ({ ...prev, limit, page: 1 }));
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
  };

  const handleCloseDetails = () => {
    setSelectedPayment(null);
  };

  const handleRefresh = () => {
    loadPayments();
    loadStats();
  };

  if (loading && payments.length === 0) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
        <div className="h-96 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment History</h1>
          <p className="text-gray-600">View and manage payment transactions</p>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Revenue
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  ${stats.totalAmount.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">Last 7 days</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Completed Payments
                </p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.completedPayments}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.successRate}% success rate
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <PaymentIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Pending Payments
                </p>
                <p className="text-2xl font-bold text-yellow-600">
                  {stats.pendingPayments}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Awaiting processing
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <RefreshCw className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Average Amount
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  ${stats.averageAmount.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Per transaction</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <PaymentFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        availableFilters={availableFilters}
        loading={loading}
      />

      {/* Payment Table */}
      <PaymentTable
        payments={payments}
        loading={loading}
        onView={handleViewPayment}
        onSort={handleSort}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
      />

      {/* Pagination */}
      <PaymentPagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalItems={pagination.total}
        itemsPerPage={paginationState.limit}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
      />

      {/* Payment Details Modal */}
      {selectedPayment && (
        <PaymentDetails
          payment={selectedPayment}
          onClose={handleCloseDetails}
        />
      )}
    </div>
  );
};

export default Payment;
